// Angular
import { Injectable, OnDestroy } from '@angular/core';

// Reactive X
import { BehaviorSubject, combineLatest, from, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

// Sentry
import * as Sentry from '@sentry/browser';

// Internal dependencies
import { createBehaviorSubject } from '../../utils/operators';

import { Bike } from '../../models/bike/bike';

import { PushService } from '../../../providers/push.service';

import { Tracker } from '../../../providers/tracker-factory.service';
import { TrackerService } from '../../../providers/tracker.service';


@Injectable({
  providedIn: 'root',
})
export class BikeService implements OnDestroy {

  /* ATTRIBUTES */

  private destroy$: Subject<void> = new Subject();

  private trackers$: Observable<readonly Tracker[]> = this.trackers.trackerList$;
  private selectedTracker$: Observable<Tracker> = this.trackers.getSelected();

  private _bikes = createBehaviorSubject(
    [],
    this.trackers$.pipe(
      map((trackers: readonly Tracker[]) => trackers.map((tracker) => new Bike(tracker))),
      distinctUntilChanged(),
      shareReplay(1),
    ),
  );
  public readonly bikes$: Observable<readonly Bike[]> = this._bikes.asObservable();

  private _selectedBike: BehaviorSubject<Bike | undefined> = createBehaviorSubject(
    undefined,
    this.selectedTracker$.pipe(
      map((tracker: Tracker | undefined) => (tracker ? new Bike(tracker) : undefined)),
      distinctUntilChanged(),
      shareReplay(1),
    ),
  );
  public readonly selectedBike$: Observable<Bike | undefined> = this._selectedBike.asObservable();


  /* LIFECYCLE */

  public constructor(
    private pushService: PushService,
    private trackers: TrackerService,
  ) {
    this.bindSelectedBikeGuardingChangeHandler();
  }

  public ngOnDestroy(): void {
    this._bikes.complete();
    this._selectedBike.complete();

    this.destroy$.next();
    this.destroy$.complete();
  }


  /* METHODS */

  private bindSelectedBikeGuardingChangeHandler(): void {
    this.selectedBike$.pipe(
      filter((bike?: Bike) => bike !== undefined),
      switchMap((bike: Bike) => combineLatest([
        bike.isGuarded$,
        bike.isStolen$,
      ])),
      takeUntil(this.destroy$),
    ).subscribe(([isGuarded, isStolen]) => {
      // Check permissions to start an alarm if the bike is
      // getting guarded because this might change it to be 'stolen'.
      if (isGuarded) {
        this.pushService.checkPermissionAndTokens();
      }

      // Stop the alarm if the bike is not stolen.
      // The alarm only activates when the bike is 'stolen'.
      // A bike is 'stolen' when it's guarded and has the 'alarm'
      // property set. Although counterintuitive, the 'alarm' property
      // doesn't solely decide if the alarm in the app is activated or not.
      if (!isStolen) {
        this.pushService.stopAlarm();
      }
    });
  }

  public selectBike(bike: Bike): void {
    this.trackers.select(bike.id);
  }

  public replaceTracker(
    newUniqueId: string,
    options: { isOldHardwareRetired?: boolean; setOnboardingAttribute?: boolean } = {
      isOldHardwareRetired: false,
      setOnboardingAttribute: false,
    },
  ): Observable<any> {
    const deviceId = this.selectedBike?.id;
    const currentUniqueId = this.selectedBike?.device?.uniqueId;

    Sentry.addBreadcrumb({
      category: 'add-biketrax',
      message: 'BikeService.replaceTracker: calling trackerExchange',
      level: Sentry.Severity.Info,
      data: { deviceId, currentUniqueId, newUniqueId, options },
    });

    const request: Observable<any> = this.trackers.trackerExchange(this.selectedBike.id, newUniqueId, options);
    if (!options.setOnboardingAttribute) {
      return request;
    } else {
      return request.pipe(
        tap(() => {
          Sentry.addBreadcrumb({
            category: 'add-biketrax',
            message: 'BikeService.replaceTracker: hardwareExchange succeeded, awaiting new uniqueId on WebSocket',
            level: Sentry.Severity.Info,
            data: { deviceId, currentUniqueId, newUniqueId },
          });
        }),
        mergeMap((res) => from(this.awaitNewUniqueId(newUniqueId)).pipe(map(() => res))),
        tap(() => {
          Sentry.addBreadcrumb({
            category: 'add-biketrax',
            message: 'BikeService.replaceTracker: new uniqueId confirmed, setting onboardingFinished=false',
            level: Sentry.Severity.Info,
            data: { deviceId, newUniqueId },
          });
        }),
        switchMap(() => this.selectedBike.tracker.setAttribute('onboardingFinished', false)),
      );
    }
  }

  public awaitSelectedBike(): Promise<Bike> {
    return this.selectedBike$
      .pipe(
        filter((bike) => bike !== undefined),
        take(1),
      )
      .toPromise();
  }

  private awaitNewUniqueId(newUniqueId: string): Promise<any> {
    return this.selectedBike$
      .pipe(
        filter((bike) => bike.device.uniqueId === newUniqueId),
        take(1),
        map(() => undefined),
      )
      .toPromise();
  }


  /* ACCESSORS */

  public get bikes(): readonly Bike[] {
    return this._bikes.getValue();
  }

  public get selectedBike(): Bike | undefined {
    return this._selectedBike.getValue();
  }
}
